import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Image as ImageIcon, PenLine, Save, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";

const empty = { title: "", category: "", excerpt: "", content: "", cover_image: "" };

export default function PostEditor() {
  const { slug } = useParams();
  const isEdit = Boolean(slug);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(!isEdit);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/posts/${slug}`)
      .then((p) => { setForm({ title: p.title, category: p.category, excerpt: p.excerpt, content: p.content, cover_image: p.cover_image || "" }); setReady(true); })
      .catch(() => { toast.error("Post not found."); navigate("/admin"); });
  }, [slug, isEdit, navigate]);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const insertAtCursor = (snippet) => {
    const el = contentRef.current;
    if (!el) {
      setForm((f) => ({ ...f, content: `${f.content}\n${snippet}\n` }));
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = `${el.value.slice(0, start)}\n${snippet}\n${el.value.slice(end)}`;
    setForm((f) => ({ ...f, content: next }));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length + 2;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const data = await api.upload("/upload", file);
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      insertAtCursor(`![${file.name.replace(/\.[^.]+$/, "")}](${fullUrl})`);
      toast.success("Image uploaded and inserted.");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't upload the image. Please try again."));
    } finally {
      setUploading(false);
    }
  };

  const handleCoverSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingCover(true);
    try {
      const data = await api.upload("/upload", file);
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, cover_image: fullUrl }));
      toast.success("Cover image set.");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't upload the cover image. Please try again."));
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const body = {
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      content: form.content.trim(),
      category: form.category.trim() || "General",
      cover_image: form.cover_image || null,
    };
    try {
      const data = isEdit ? await api.put(`/posts/${slug}`, body) : await api.post("/posts", body);
      toast.success(isEdit ? "Post updated!" : "Post published!");
      navigate(`/blog/${data.slug}`);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't save the post. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="post-editor-page">
      <Link to="/admin" data-testid="post-editor-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-stone-500 transition-colors hover:text-amber-700">
        <ArrowLeft className="h-4 w-4" /> Back to admin
      </Link>
      <h1 className="mt-8 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900" data-testid="post-editor-heading">
        {isEdit ? "Edit post" : "Write a new post"}
      </h1>
      <p className="mt-3 text-base text-stone-600">Markdown is supported in the content. Changes appear on the blog immediately.</p>

      {ready && (
        <form onSubmit={handleSubmit} className="mt-10 space-y-6" data-testid="post-editor-form">
          <div className="space-y-2">
            <Label htmlFor="post-title">Title</Label>
            <Input id="post-title" required value={form.title} onChange={update("title")} placeholder="My next big idea" data-testid="post-title-input" className="bg-white" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="post-category">Category</Label>
            <Input id="post-category" value={form.category} onChange={update("category")} placeholder="AI, Growth, AdTech, Tools..." data-testid="post-category-input" className="bg-white" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="post-excerpt">Excerpt</Label>
            <Textarea id="post-excerpt" required value={form.excerpt} onChange={update("excerpt")} placeholder="A one-line summary shown on the blog listing" rows={2} data-testid="post-excerpt-input" className="bg-white" />
          </div>
          <div className="space-y-2">
            <Label>Cover image (shown on the blog listing card and post header)</Label>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              className="hidden"
              onChange={handleCoverSelect}
              data-testid="post-cover-input"
            />
            {form.cover_image ? (
              <div className="relative w-full max-w-xs">
                <img src={form.cover_image} alt="Cover" data-testid="post-cover-preview" className="aspect-video w-full rounded-xl object-cover border border-stone-200" />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, cover_image: "" }))}
                  data-testid="post-cover-remove-button"
                  className="absolute -top-2 -right-2 rounded-full bg-stone-900 p-1.5 text-white hover:bg-red-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploadingCover}
                onClick={() => coverInputRef.current?.click()}
                data-testid="post-cover-upload-button"
                className="gap-2 rounded-full text-xs font-semibold"
              >
                <ImageIcon className="h-3.5 w-3.5" /> {uploadingCover ? "Uploading..." : "Add cover image"}
              </Button>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="post-content">Content (Markdown)</Label>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  onChange={handleImageSelect}
                  data-testid="post-image-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="post-image-upload-button"
                  className="gap-2 rounded-full text-xs font-semibold"
                >
                  <ImageIcon className="h-3.5 w-3.5" /> {uploading ? "Uploading..." : "Insert image"}
                </Button>
              </div>
            </div>
            <Textarea
              id="post-content"
              ref={contentRef}
              required
              value={form.content}
              onChange={update("content")}
              placeholder="Write your story here... You can also paste an external image URL as ![alt](url)"
              rows={16}
              data-testid="post-content-input"
              className="bg-white font-mono text-sm"
            />
            <p className="text-xs text-stone-400">Use the button to upload an image, or paste one as Markdown: ![alt text](https://image-url.jpg)</p>
          </div>
          <Button type="submit" disabled={loading} data-testid="post-editor-submit-button" className="rounded-full bg-amber-600 px-8 font-semibold text-white hover:bg-amber-700 gap-2">
            {isEdit ? <Save className="h-4 w-4" /> : <PenLine className="h-4 w-4" />}
            {loading ? "Saving..." : isEdit ? "Save changes" : "Publish post"}
          </Button>
        </form>
      )}
    </div>
  );
}
